import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HashLoader } from "react-spinners";
import {
  CaretLeft,
  CheckCircle,
  Clock,
  CurrencyCircleDollar,
  SpinnerGap,
  XCircle,
  X,
  Printer
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import useQuickLoan from "../../hooks/useQuickLoan";
import useLoanRepaymentSchedule from "../../hooks/useLoanRepaymentSchedule";
import useLoanRepayment from "../../hooks/useLoanRepayment";
import { formatAsMoney } from "../../helpers/numbers";
import { formatPaymentDue } from "../../helpers/dates";
import { getStatusColor } from "../../helpers/colors";
import { normalizeRepaymentSchedule, isInstallmentPaid, parseScheduleAmount } from "../../helpers/loanSchedule";
import OfferLetter from "../../components/OfferLetter";
import { useAuth } from "../../context/AuthContext";

/**
 * Loan details page.
 * Shows loan summary and payment schedule when loan has loan_account_number (fetched from Alert API).
 */
const LoanDetails = () => {
  const { loanId } = useParams<{ loanId: string }>();
  const [isOfferLetterModalOpen, setIsOfferLetterModalOpen] = useState(false);
  const [repaymentToConfirm, setRepaymentToConfirm] = useState<{
    title: string;
    amount: number;
    isTermination: boolean;
    description: string;
  } | null>(null);
  const { refetch } = useAuth();

  const { loan, isLoadingLoan, isLoanError, refetchLoan } = useQuickLoan(
    loanId ?? ""
  );

  const hasLoanAccountNumber = !!loan?.loan_account_number;

  const {
    schedule,
    isLoadingSchedule,
    isScheduleError,
    refetchSchedule,
  } = useLoanRepaymentSchedule(loanId ?? "", {
    enabled: !!loanId && hasLoanAccountNumber,
  });

  const { repay, repaying, isRepayError, repayError } = useLoanRepayment({
    onSuccess() {
      toast.success("Loan repayment successful");
      setRepaymentToConfirm(null);
      refetch();
      refetchLoan();
      refetchSchedule();
    },
    onError(error) {
      toast.error(error?.message || "Loan repayment failed");
      refetchSchedule();
      refetchLoan();
    },
  });

  const scheduleToShow = useMemo(() => {
    if (hasLoanAccountNumber && schedule.length > 0) return schedule;
    return normalizeRepaymentSchedule(loan?.repayment_schedule ?? null);
  }, [hasLoanAccountNumber, schedule, loan?.repayment_schedule]);

  const status = (loan?.status ?? "").toLowerCase();
  const isPending = status === "pending";
  const isDeclined = status === "declined";
  const isApproved = status === "approved" || status === "disbursed";

  const paidCount = useMemo(
    () =>
      scheduleToShow.filter(isInstallmentPaid).length,
    [scheduleToShow]
  );
  const totalInstallments = scheduleToShow.length;
  const isCompleted = totalInstallments > 0 && paidCount >= totalInstallments;

  const totalAmount = Number(loan?.loan_amount ?? 0);
  const industry = (loan?.industry ?? "BNPL").replace(/^\w/, (c) =>
    c.toUpperCase()
  );
  const nextUnpaidInstallment = useMemo(
    () =>
      scheduleToShow.find(
        (row) =>
          !isInstallmentPaid(row)
      ),
    [scheduleToShow]
  );
  const nextInstallmentAmount = useMemo(() => {
    return parseScheduleAmount(nextUnpaidInstallment?.RemainingTotal);
  }, [nextUnpaidInstallment?.RemainingTotal]);
  const outstandingBalance = useMemo(
    () =>
      scheduleToShow.reduce((sum, row) => {
        return isInstallmentPaid(row) ? sum : sum + parseScheduleAmount(row.RemainingTotal);
      }, 0),
    [scheduleToShow]
  );
  const canShowRepaymentActions =
    isApproved && !isCompleted && scheduleToShow.length > 0;
  const hasConfirmedOutstanding = scheduleToShow.every((row) => row.RemainingTotal != null);
  const hasEnoughForNext =
    !isLoadingSchedule && !isScheduleError && schedule.length > 0 && nextInstallmentAmount > 0;
  const hasEnoughToClear =
    hasEnoughForNext && outstandingBalance > 0 && hasConfirmedOutstanding;

  const confirmRepayment = useCallback(() => {
    if (!repaymentToConfirm || !loanId || repaying) return;

    repay({
      loan_id: loanId,
      repayment_amount: repaymentToConfirm.amount,
      is_termination: repaymentToConfirm.isTermination,
    });
  }, [repay, repaymentToConfirm, loanId, repaying]);

  if (isLoadingLoan || !loanId) {
    return (
      <div className="grid place-items-center min-h-[40vh]">
        <HashLoader size={40} />
      </div>
    );
  }

  if (isLoanError || !loan) {
    return (
      <div className="px-5 md:px-10 py-8 flex flex-col items-center gap-6">
        <Link
          to="/payments/loans"
          className="p-2 py-3 text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start hover:bg-gray-50"
        >
          <CaretLeft weight="bold" />
          Back to loans
        </Link>
        <div className="text-center text-gray-600">
          <p className="font-medium">Loan not found</p>
          <p className="text-sm mt-1">
            This loan may have been removed or you don&#39;t have access to it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full flex flex-col gap-6 px-5 md:px-10 py-6 pb-12">
      {repaying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      <Link
        to="/payments/loans"
        className="p-2 py-3 text-deepBlue border px-6 rounded-xl font-medium inline-flex gap-2 items-center border-gray-300 self-start hover:bg-gray-50 transition-colors"
      >
        <CaretLeft weight="bold" />
        Back to loans
      </Link>

      <div className="flex flex-col gap-6 max-w-3xl mx-auto w-full">
        {/* Loan summary card */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Pay in 3 loan for {industry}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {loan?.created_at
                  ? formatPaymentDue(loan.created_at)
                  : "—"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium w-fit"
                style={{
                  backgroundColor: getStatusColor(status).bg,
                  color: getStatusColor(status).text,
                }}
              >
                {status || "—"}
              </div>
              <button
                onClick={() => setIsOfferLetterModalOpen(true)}
                className="px-4 py-2 border border-gray-300 text-deepBlue font-medium text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                View Offer Letter
              </button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gray-100">
                <CurrencyCircleDollar size={28} className="text-deepBlue" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Loan amount</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₦ {formatAsMoney(totalAmount)}
                </p>
              </div>
            </div>
          </div>

          {/* Status-specific messaging */}
          {isPending && (
            <div className="mt-6 flex gap-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex-shrink-0 p-2 rounded-full bg-amber-100">
                <Clock size={24} className="text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-900">
                  Your loan request is pending
                </p>
                <p className="text-sm text-amber-800 mt-1">
                  We’re reviewing your application. You’ll be notified once a
                  decision is made. This usually takes a few minutes.
                </p>
              </div>
            </div>
          )}

          {isDeclined && (
            <div className="mt-6 flex gap-4 p-4 rounded-xl bg-red-50 border border-red-200">
              <div className="flex-shrink-0 p-2 rounded-full bg-red-100">
                <XCircle size={24} className="text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-900">
                  Your loan request was declined
                </p>
                <p className="text-sm text-red-800 mt-1">
                  We’re unable to approve this application. You can try again
                  later or contact support for more information.
                </p>
              </div>
            </div>
          )}

          {isCompleted && isApproved && (
            <div className="mt-6 flex gap-4 p-4 rounded-xl bg-green-50 border border-green-200">
              <div className="flex-shrink-0 p-2 rounded-full bg-green-100">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-900">
                  Loan fully repaid
                </p>
                <p className="text-sm text-green-800 mt-1">
                  All installments have been paid. Thank you for your business.
                </p>
              </div>
            </div>
          )}
        </section>

        {canShowRepaymentActions && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-lg font-semibold text-gray-900">
                Repayment options
              </h2>
              <p className="text-sm text-gray-500">
                Pay your next installment when due, or clear the remaining
                balance now.
              </p>
            </div>

            <div className="mt-5 rounded-xl bg-gray-50 border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Paid from your linked Alert account</p>
                <p className="text-sm text-gray-500">
                  We check its available balance before payment. Your Visaro wallet balance is separate.
                </p>
              </div>
              {isRepayError && (
                <p className="text-sm text-red-600">
                  {repayError?.message || "Loan repayment failed"}
                </p>
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-200 p-5 flex flex-col gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Pay next installment
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Covers the next unpaid payment on your schedule.
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-2xl font-bold text-deepBlue">
                    {nextUnpaidInstallment?.RemainingTotal != null ? `₦ ${formatAsMoney(nextInstallmentAmount)}` : "Awaiting bank confirmation"}
                  </p>
                  {nextUnpaidInstallment?.PaymentDueDate && (
                    <p className="text-xs text-gray-500 mt-1">
                      Due {formatPaymentDue(nextUnpaidInstallment.PaymentDueDate)}
                    </p>
                  )}
                </div>
                {!hasEnoughForNext && (
                  <p className="text-sm text-red-600">
                    Refresh your loan schedule to confirm the amount before paying.
                  </p>
                )}
                <button
                  type="button"
                  disabled={!hasEnoughForNext || repaying}
                  onClick={() =>
                    setRepaymentToConfirm({
                      title: "Pay next installment",
                      amount: nextInstallmentAmount,
                      isTermination: false,
                      description:
                        "This will repay the next unpaid installment on your loan.",
                    })
                  }
                  className="mt-auto p-3 rounded-xl bg-deepBlue text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Pay next installment
                </button>
              </div>

              <div className="rounded-2xl border border-visaro-orange/30 bg-visaro-orange/5 p-5 flex flex-col gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Clear outstanding balance
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Pay all remaining installments at once and close out the
                    loan.
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Outstanding balance</p>
                  <p className="text-2xl font-bold text-visaro-orange">
                    {hasConfirmedOutstanding ? `₦ ${formatAsMoney(outstandingBalance)}` : "Awaiting bank confirmation"}
                  </p>
                </div>
                {!hasEnoughToClear && (
                  <p className="text-sm text-red-600">
                    We need a confirmed outstanding amount before you can clear this loan.
                  </p>
                )}
                <button
                  type="button"
                  disabled={!hasEnoughToClear || repaying}
                  onClick={() =>
                    setRepaymentToConfirm({
                      title: "Clear outstanding balance",
                      amount: outstandingBalance,
                      isTermination: true,
                      description:
                        "This will attempt to repay the full outstanding balance and close out this loan.",
                    })
                  }
                  className="mt-auto p-3 rounded-xl bg-visaro-orange text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear balance
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Payment schedule */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Payment schedule
          </h2>
          {hasLoanAccountNumber && <button type="button" disabled={isLoadingSchedule || repaying} onClick={() => refetchSchedule()} className="text-sm text-deepBlue underline mb-4">Refresh schedule</button>}

          {isLoadingSchedule && hasLoanAccountNumber ? (
            <div className="flex items-center justify-center py-12">
              <HashLoader size={32} />
            </div>
          ) : isScheduleError && scheduleToShow.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>Unable to load payment schedule.</p>
              <button type="button" onClick={() => refetchSchedule()} className="mt-3 text-deepBlue underline">Try again</button>
            </div>
          ) : scheduleToShow.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>No payment schedule available.</p>
              {isPending && (
                <p className="text-sm mt-1">
                  The schedule will appear once your loan is approved.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {isScheduleError && <p className="text-sm text-amber-700 mb-3">Showing your saved schedule. Refresh it before making a payment. <button type="button" onClick={() => refetchSchedule()} className="underline">Refresh</button></p>}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      #
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      Due date
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      Principal
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">
                      Interest
                    </th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600">
                      Total
                    </th>
                    <th className="text-center py-3 px-2 font-medium text-gray-600">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleToShow.map((row, idx) => {
                    const paid = isInstallmentPaid(row);
                    return (
                      <tr
                        key={row.Id ?? idx}
                        className="border-b border-gray-100 hover:bg-gray-50/50"
                      >
                        <td className="py-4 px-2 text-gray-700">{idx + 1}</td>
                        <td className="py-4 px-2 text-gray-700">
                          {formatPaymentDue(row.PaymentDueDate ?? "")}
                        </td>
                        <td className="py-4 px-2 text-gray-700">
                          {row.Principal != null ? `₦ ${formatAsMoney(parseScheduleAmount(row.Principal))}` : "—"}
                        </td>
                        <td className="py-4 px-2 text-gray-700">
                          {row.Interest != null ? `₦ ${formatAsMoney(parseScheduleAmount(row.Interest))}` : "—"}
                        </td>
                        <td className="py-4 px-2 text-right font-medium">
                          ₦ {formatAsMoney(parseScheduleAmount(row.Total))}
                        </td>
                        <td className="py-4 px-2 text-center">
                          {paid ? (
                            <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                              <CheckCircle size={16} weight="fill" />
                              Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                              <Clock size={16} weight="fill" />
                              {row.status === "partial" ? "Partly paid" : "Pending"}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {scheduleToShow.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-sm text-gray-600">
              <span>
                {paidCount} of {totalInstallments} paid
              </span>
              <span>
                Total: ₦{" "}
                {formatAsMoney(
                  scheduleToShow.reduce(
                    (sum, r) => sum + parseScheduleAmount(r.Total),
                    0
                  )
                )}
              </span>
            </div>
          )}
        </section>
      </div>

      {/* Offer Letter Modal Overlay */}
      <AnimatePresence>
        {repaymentToConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 flex flex-col gap-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {repaymentToConfirm.title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {repaymentToConfirm.description}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setRepaymentToConfirm(null)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>

              <div className="rounded-xl bg-gray-50 p-4 border border-gray-100">
                <p className="text-sm text-gray-500">Amount to pay</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₦ {formatAsMoney(repaymentToConfirm.amount)}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setRepaymentToConfirm(null)}
                  className="p-3 rounded-xl border border-gray-300 text-deepBlue font-medium flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={repaying}
                  onClick={confirmRepayment}
                  className="p-3 rounded-xl bg-deepBlue text-white font-medium flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {repaying && <SpinnerGap className="animate-spin" />}
                  Confirm payment
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {isOfferLetterModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-6"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-y-auto"
            >
              {/* Sticky Header with Close Button and Print Data inside OfferLetter */}
              <div className="sticky top-0 z-10 flex items-center justify-between bg-white border-b border-gray-200 px-6 py-4">
                <h2 className="text-lg font-bold text-gray-900">Offer Letter</h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      // target the specific Offer Letter container and call print
                      const printContent = document.getElementById("offer-letter-print-zone");
                      if (printContent) {
                        const originalContents = document.body.innerHTML;
                        const printContents = printContent.innerHTML;
                        document.body.innerHTML = printContents;
                        window.print();
                        document.body.innerHTML = originalContents;
                        window.location.reload(); // brutal but ensures react re-mounts cleanly
                      } else {
                        window.print();
                      }
                    }}
                    className="flex items-center gap-2 bg-deepBlue text-white px-4 py-2 rounded-lg shadow hover:bg-deepBlue/90 transition-all font-medium text-sm"
                  >
                    <Printer size={18} />
                    <span className="hidden sm:inline">Print / Save as PDF</span>
                  </button>
                  <button
                    onClick={() => setIsOfferLetterModalOpen(false)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X size={24} weight="bold" />
                  </button>
                </div>
              </div>

              {/* Pluggable Print Container */}
              <div id="offer-letter-print-zone" className="p-4 md:p-8">
                <OfferLetter loanProp={loan} scheduleProp={scheduleToShow} isEmbedded />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoanDetails;
